import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { createAppTheme, type AppThemeMode } from './index';
import {
  getInitialThemeMode,
  THEME_MODE_STORAGE_KEY,
  startBodyOverflowObserver,
} from './themeMode.utils';
import { ThemeModeContext } from './ThemeModeContext';
import { CssBaseline, ThemeProvider } from '@mui/material';

export const ThemeModeProvider = ({ children }: PropsWithChildren) => {
  const [mode, setMode] = useState<AppThemeMode>(getInitialThemeMode);

  useEffect(() => {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    document.documentElement.setAttribute('data-theme-mode', mode);
  }, [mode]);

  useEffect(() => {
    const cleanup = startBodyOverflowObserver();
    return cleanup;
  }, []);

  const toggleMode = useCallback(() => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggleMode,
    }),
    [mode, toggleMode]
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
